import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSnackbar } from 'notistack'
import { faCircleExclamation, faEye, faLock, faLockOpen, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans, useTranslation } from 'react-i18next'

import { useOnlineStatus } from '../Hooks/useOnlineStatus'
import { useMutationQueue } from '../../offline/useMutationQueue'
import { useSyncProgress } from '../../offline/syncStatus'
import { offlineDb, type SyncIssue } from '../../offline/db'
import { RootState } from '../../state/store'
import { setSelectedDate, setSelectedShift, setShiftEditability } from '../../state/reducers/shiftReducer'
import { settingsAjax } from '../../ajax/SettingsAjax'
import { ErrorResponse } from '../../DTOs/Common/ErrorResponse'
import { createAppRoutes, pageEditing } from '../../utilities/appRoutes'
import { pageOf, routeChainAt } from '../../utilities/helpers/routeAccess'
import { useGlobalModal } from '../Contexts/GlobalModalContext'
import ConflictResolutionModal from './ConflictResolutionModal'
import SyncIssuesModal from './SyncIssuesModal'
import { resolveStatusConditions, StatusCondition, StatusTone } from './statusBannerState'
import { useDrillBlastModules } from '../Hooks/useDrillBlastSettings';
import i18n from '../../i18n'

const PINNED_CLASSES = 'sticky top-0 z-20'

const ROW_CLASSES = 'w-full shrink-0 overflow-hidden flex flex-nowrap items-center justify-center gap-2 px-4 py-2 compact:py-1 text-sm font-medium border-b transition-colors'

const TONE_CLASSES: Record<StatusTone, string> = {
  danger: 'bg-theme-danger text-theme-primary-contrast border-theme-danger-hover',
  info: 'bg-theme-info text-theme-primary-contrast border-theme-info-hover',
  warning: 'bg-theme-warning text-theme-primary-contrast border-theme-warning-hover',
  neutral: 'bg-theme-surface-2 text-theme-text border-theme-border',
}

const CHIP_CLASSES: Record<StatusTone, string> = {
  danger: 'bg-white/25',
  info: 'bg-white/25',
  warning: 'bg-white/25',
  neutral: 'bg-theme-bg',
}

const CONTROL_CLASSES = 'h-5 shrink-0 inline-flex items-center gap-1 rounded-full px-2 text-xs font-bold leading-none'

const shiftLabel = (shift: string) => i18n.t(shift === 'DAY' ? 'shifts.DAY' : shift === 'NIGHT' ? 'shifts.NIGHT' : 'shifts.AFTERNOON')

const WIDE = <span className="hidden sm:inline" />

function conditionIcon(condition: StatusCondition) {
  if (condition.kind === 'syncing') return faSpinner
  if (condition.kind === 'syncIssues') return faTriangleExclamation
  if (condition.kind === 'shiftLocked') return faLock
  if (condition.kind === 'viewOnly') return faEye
  return faCircleExclamation
}

function chipLabel(condition: StatusCondition): string {
  switch (condition.kind) {
    case 'syncing': return i18n.t('status.chipSyncing')
    case 'offline': return i18n.t('status.chipOffline')
    case 'syncIssues': return i18n.t('status.chipIssues', { count: condition.count })
    case 'pending': return i18n.t('status.chipPending', { count: condition.count })
    case 'shiftLocked': return i18n.t(condition.exists ? 'status.chipShiftLocked' : 'status.chipShiftNotSynced')
    case 'viewOnly': return i18n.t('status.chipViewOnly')
  }
}

const StatusBanner = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { showModal, hideModal } = useGlobalModal()
  const isOnline = useOnlineStatus()
  const { pendingCount } = useMutationQueue()
  const { isSyncing, total, done } = useSyncProgress()
  const [isUnlocking, setIsUnlocking] = useState(false)
  const user = useSelector((state: RootState) => state.authReducer.user)
  const telemetryEnabled = useSelector((state: RootState) => state.authReducer.roleConfig?.telemetryEnabled)
  const drillBlastModules = useDrillBlastModules();
  const routes = useMemo(() => createAppRoutes({ telemetryEnabled, drillBlastModules }), [telemetryEnabled, drillBlastModules])
  const page = pageOf(routeChainAt(routes, pathname))

  const issuesRaw = useLiveQuery(() => offlineDb.syncIssues.orderBy('createdAt').toArray(), [])
  const {
    isLocked, canToggleLock, shiftExists, selectedDate, selectedShift, provisionalShiftDate, provisionalShift,
  } = useSelector((state: RootState) => state.shift)

  const prevIssueCountRef = useRef<number>(-1)
  useEffect(() => {
    if (issuesRaw === undefined) return
    const prev = prevIssueCountRef.current
    if (prev >= 0 && issuesRaw.length > prev) {
      const added = issuesRaw.length - prev
      enqueueSnackbar(
        t('status.issuesAdded', { count: added }),
        { variant: 'warning', autoHideDuration: 8000 }
      )
    }
    prevIssueCountRef.current = issuesRaw.length
  }, [issuesRaw, enqueueSnackbar, t])

  const openConflictModal = (issue: SyncIssue) => {
    showModal({
      content: () => <ConflictResolutionModal issue={issue} onClose={hideModal} />,
      tailwindMaxWidthClass: 'max-w-2xl',
    })
  }

  const openIssuesModal = () => {
    showModal({
      content: () => <SyncIssuesModal onClose={hideModal} onResolveConflict={openConflictModal} />,
      tailwindMaxWidthClass: 'max-w-3xl',
    })
  }

  const returnToProvisionalShift = () => {
    if (!provisionalShiftDate || !provisionalShift) return
    dispatch(setSelectedDate(provisionalShiftDate))
    dispatch(setSelectedShift(provisionalShift))
  }

  const unlockShift = async () => {
    const date = DateTime.fromISO(selectedDate)
    const backendCode = selectedShift === 'DAY' ? 'D' : 'N'
    setIsUnlocking(true)
    try {
      await settingsAjax.unlockShift(date, backendCode)
      const res = await settingsAjax.getShiftEditability(date, backendCode)
      if (!ErrorResponse.isErrorResponse(res)) dispatch(setShiftEditability(res))
    } finally {
      setIsUnlocking(false)
    }
  }

  const conditions = resolveStatusConditions({
    isOnline,
    isSyncing,
    syncTotal: total,
    syncDone: done,
    pendingCount,
    syncIssueCount: issuesRaw?.length ?? 0,
    isShiftPage: !!page?.isShiftPage,
    isShiftLocked: isLocked,
    shiftExists,
    canToggleLock,
    selectedDate,
    selectedShift,
    provisionalShiftDate,
    provisionalShift,
    isViewOnly: pageEditing(page, user) === 'viewOnly',
  })

  const [primary, ...secondaries] = conditions

  if (!primary) {
    return <div aria-hidden className={`${ROW_CLASSES} border-transparent`}>&nbsp;</div>
  }

  const message = (() => {
    switch (primary.kind) {
      case 'syncing':
        return (
          <>
            {primary.done > 0 && primary.done < primary.total
              ? t('status.syncProgress', { done: primary.done, total: primary.total })
              : t('status.syncCount', { count: primary.total })}
            <span className="hidden sm:inline">{t('status.syncCarryOn')}</span>
          </>
        )
      case 'offline':
        return (
          <>
            <span className="hidden sm:inline">{t('status.offlineLong')}</span>
            <span className="sm:hidden">{t('status.offlineShort')}</span>
          </>
        )
      case 'syncIssues':
        return t('status.syncIssues', { count: primary.count })
      case 'pending':
        return t('status.pending', { count: primary.count })
      case 'shiftLocked':
        return (
          <Trans
            i18nKey={primary.exists ? 'status.lockedShift' : 'status.unsyncedShift'}
            values={{ when: `${DateTime.fromISO(primary.dateIso).toFormat('dd MMM yyyy')} · ${shiftLabel(primary.shift)}` }}
            components={{ strong: <strong />, wide: WIDE }}
          />
        )
      case 'viewOnly':
        return (
          <Trans i18nKey="status.viewOnly" components={{ strong: <strong />, wide: WIDE }} />
        )
    }
  })()

  const chipClasses = `${CONTROL_CLASSES} ${CHIP_CLASSES[primary.tone]}`
  const buttonClasses = `${chipClasses} hover:bg-white/40 cursor-pointer transition`

  const action = (condition: StatusCondition) => {
    switch (condition.kind) {
      case 'offline':
        return condition.returnTo && (
          <button type="button" onClick={returnToProvisionalShift} className={buttonClasses}>
            {t('status.backTo', { date: DateTime.fromISO(condition.returnTo.date).toFormat('dd MMM'), shift: shiftLabel(condition.returnTo.shift) })}
          </button>
        )
      case 'syncIssues':
        return <button type="button" onClick={openIssuesModal} className={buttonClasses}>{t('status.review')}</button>
      case 'shiftLocked':
        return condition.canUnlock && (
          <button
            type="button"
            onClick={unlockShift}
            disabled={isUnlocking}
            className={`${CONTROL_CLASSES} bg-theme-warning hover:bg-theme-warning-hover disabled:opacity-50 text-theme-primary-contrast border border-theme-warning-hover cursor-pointer transition`}
          >
            <FontAwesomeIcon icon={faLockOpen} />
            {isUnlocking ? t('actions.unlocking') : t('actions.unlock')}
          </button>
        )
      default:
        return null
    }
  }

  return (
    <div className={`${ROW_CLASSES} ${PINNED_CLASSES} ${TONE_CLASSES[primary.tone]}`}>
      <FontAwesomeIcon icon={conditionIcon(primary)} spin={primary.kind === 'syncing'} className="shrink-0" />
      <span className="min-w-0 truncate">{message}</span>

      {primary.kind === 'offline' && primary.pendingCount > 0 && (
        <span className={chipClasses}>{t('status.chipPending', { count: primary.pendingCount })}</span>
      )}

      {action(primary)}

      {secondaries.map(condition => (
        <Fragment key={condition.kind}>
          <span className={chipClasses}>
            <FontAwesomeIcon icon={conditionIcon(condition)} spin={condition.kind === 'syncing'} />
            {chipLabel(condition)}
          </span>
          {action(condition)}
        </Fragment>
      ))}
    </div>
  )
}

export default StatusBanner
